import _get from 'lodash/get';
import _kebabCase from 'lodash/kebabCase';
import { Pixall, Google } from '../analytics/index.js';
import { addListingTriggerData, getInventoryOwnerById, getListingCategory, isPremiumSpotlight, isVDP, cleanValues } from '../utilities/index.js';
import { DataLayerTransformer, productConfigTransformer as pixallProductConfigTransformer, inventoryTransformer as PixallInventoryTransformer, listingTypeUtils } from '../transformers/pixall/index.js';
import { ownerWebsiteClickHelper, sanitizeValue } from './helpers/index.js';
import { inventoryTransformer as googleInventoryTransformer, productConfigTransformer as googleProductConfigTransformer, pageNameTransformer } from '../transformers/google/index.js';
import { getCDLVehicleTransformer } from '../transformers/pixall/inventoryTransformer';
import { getCDLVehicleSearch } from '../transformers/pixall/DataLayerTransformer';
import { valueEventCode } from '../constants/index.js';
const {
  transformProductConfig,
  hasPrice
} = googleProductConfigTransformer;
const {
  getProductConfiguration
} = pixallProductConfigTransformer;

/**
 * @summary Sends Pixall impression for selected owner (DDP)
 * @data activeDuck, ownerDuck
 */
export function ownerImpression(_ref, option) {
  let {
    dataLayer = {},
    state,
    activeDuck,
    ownersDuck,
    history
  } = _ref;
  if (activeDuck && ownersDuck) {
    const page = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
    const activeOwnerId = activeDuck.selectors.getDuckState(state);
    const owner = ownersDuck.selectors.getOwners(state)[activeOwnerId];
    Pixall.sendImpression({
      dataLayer,
      history
    }, {
      label: page,
      listingCode: activeOwnerId,
      eventSource: 'vehicle',
      zip: _get(owner, 'location.address.zip', '')
    }, option);
  } else {
    throw new Error('activeDuck and/or ownersDuck is missing');
  }
  return {};
}

/**
 * @summary sends Pixall impression for owners
 * @data ownersDuck or ownerIds, resultsDuck
 */
export function ownerImpressions(_ref2, option) {
  let {
    data = {},
    dataLayer = {},
    state,
    resultsDuck,
    history
  } = _ref2;
  let response = {};
  const {
    ownerIds = [],
    pixallData
  } = data;
  const activeOwnerIds = resultsDuck ? resultsDuck.selectors.getActiveResults(state) : ownerIds || [];
  const pageName = _get(dataLayer, 'page.pageName', '');
  if (pixallData && activeOwnerIds?.length > 0) {
    const {
      isList,
      ...restPixallData
    } = pixallData;
    Pixall.sendImpression({
      dataLayer,
      history
    }, {
      eventSource: isList ? 'vehicle-list' : 'vehicle',
      description: pageName === 'fyd_srl' ? undefined : activeOwnerIds,
      label: pixallData.label ? pixallData.label : '',
      ...restPixallData
    }, option);
    response = {
      impressions: activeOwnerIds
    };
  } else {
    throw new Error('resultsDuck/pixallData is missing');
  }
  return response;
}

/**
 * @deprecated
 */
export async function ownerClick() {
  return {};
}

/**
 * @summary Sends event when owner logo image is clicked
 */
export async function ownerLogoClick(_ref3, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref3;
  const brand = _get(state, 'brand', '');
  const {
    par,
    pixallData = {},
    cmp: customCmp,
    ownerId = '',
    inventoryId
  } = data;
  const cmp = customCmp || 'c_lgo';
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const inventory = getInventoryOwnerById(state, inventoryId);
  const listingCategory = getListingCategory(par);
  const clickType = _get(state, 'query.clickType', listingCategory);
  const {
    isCarousel,
    ...restPixallData
  } = pixallData;
  const cdl = {
    eventProperties: {
      vehicle: PixallInventoryTransformer.getVehicleCommonDataLayer(inventory),
      event: {
        valueEventCode: '0AAD'
      }
    }
  };
  Pixall.sendClickEvent({
    label: `Click to Dealer Site - ${cmp}` + (brand !== 'kbb' ? ` - ${pageId}` : ''),
    eventSource: isCarousel ? 'vehicle-carousel' : 'dealership',
    feature: 'Drive the 5',
    listingCode: ownerId,
    hasListingType: true,
    inventory,
    listingType: listingCategory,
    ...restPixallData,
    cdl
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventResult: `sitelink::${clickType}`,
    eventData: {
      vehicle: {
        clickType,
        dealershipId: ownerId?.toString()
      }
    }
  }, option);

  // return data for custom event
  return {
    cmp,
    par
  };
}

/**
 * @summary Sends event when accelerate tile is clicked
 * @data dataLayer
 */
export async function ownerAccelerateClick(_ref4, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref4;
  const {
    par,
    pixallData = {},
    cmp: customCmp,
    ownerId = '',
    inventoryId
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const cmp = customCmp || 'accl_btn';
  const {
    isCarousel,
    ...restPixallData
  } = pixallData;
  const parent = par ? `_${par}` : '';
  Pixall.sendClickEvent({
    label: `Click Accelerate Button - ${cmp}${parent}`,
    eventSource: isCarousel ? 'vehicle-carousel' : 'dealership',
    // feature: 'Drive the 9',
    inventory: inventoryWithOwner,
    listingCode: ownerId,
    par,
    ...restPixallData
  }, dataLayer, option);

  // return data for custom event
  return {
    cmp,
    par
  };
}

/**
 * @summary Sends event when call dealer button is clicked
 * @data inventoryId, ownerId
 */
export async function ownerCall(_ref5, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref5;
  const {
    includePageId = true,
    inventoryId,
    par,
    clickType,
    position,
    placement,
    pixallData = {},
    selectedTab = 'service',
    selectedVrsData = {},
    trimSelected,
    owner,
    listingType
  } = data;
  const isWallet = placement === 'myWallet';
  const {
    payments
  } = state;
  const {
    interactions
  } = payments;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const {
    hasUpdatedNDSCalc,
    hasOpenedWallet,
    hasOpenedTradeIn
  } = interactions;
  const includeWalletData = hasUpdatedNDSCalc || hasOpenedWallet || hasOpenedTradeIn;
  const callSellerPar = 'call_seller';
  let goal;
  let ddpValueEventCode;
  let customProductConfiguration;
  if (includeWalletData || par === callSellerPar) {
    goal = 'Call with My Wallet Data';
  }
  let cmp = 'call_slr';
  if (par?.includes('alp')) {
    cmp = 'call_slr_a';
  }
  const labelSuffix = includePageId ? ` - ${pageId}` : '';
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  let eventElement = 'dealer';
  let eventResult = 'lead::phone';
  let label = `Click to Call - ${cmp}${labelSuffix}`;
  let leadType = 'call';
  const {
    productConfiguration
  } = transformProductConfig({
    homeServices: _get(owner, 'homeServices', {}),
    wallet: includeWalletData,
    nds: hasPrice(inventoryWithOwner)
  });
  switch (pageId) {
    case 'fyc_srl':
    case 'fyc_ncf':
      eventResult = `lead::phone::${clickType}`;
      break;
    case 'fyd_ddp':
    case 'dlrinfoinvovrnew':
      eventResult = position ? `lead::phone_${position}` : 'lead::phone_dealer';
      label = `lead_phone_${position} - ${pageId}`;
      inventoryWithOwner.owner = data.owner;
      eventElement = selectedTab;
      ddpValueEventCode = valueEventCode.PHONE_LEAD;
      leadType = 'form';
      customProductConfiguration = {
        vehicle: {
          productConfiguration
        }
      };
      break;
    default:
      break;
  }
  if (placement === 'sticky-header' || placement === 'media-gallery') {
    eventResult = `lead::phone::${placement}`;
    label = `${label} - ${placement}`;
  }
  const ownerId = _get(inventoryWithOwner, 'owner.id') || data.ownerId || '';
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, true, includeWalletData);
  const isBuildAndPrice = pageId === 'bld_and_pr';
  if (isBuildAndPrice) {
    eventResult = 'lead::phone::build-price';
  }

  /**
   * Dimension 135: clickType
   */
  // remove productConfiguration that cache by sent email before.
  const eventData = {
    vehicle: {
      ...vehicleData,
      dealershipId: ownerId?.toString()
    }
  };
  if (pageId === 'fyc_srl') eventData.vehicle.clickType = clickType;
  let googleProps = {
    eventElement,
    eventAction: 'submit',
    eventResult,
    eventData
  };
  const includeMyWallet = getProductConfiguration(inventoryWithOwner, includeWalletData)?.includes('my-wallet');
  const getCustomValueEventCode = () => {
    if (isBuildAndPrice) {
      return includeMyWallet ? valueEventCode.BAP_PHONE_LEAD_MY_WALLET : valueEventCode.PHONE_LEAD;
    }
    return includeMyWallet ? valueEventCode.PHONE_LEAD_WITH_MY_WALLET : valueEventCode.PHONE_LEAD;
  };
  const labelCallSeller = 'Click to Call - call_slr';
  const cdl = {
    eventProperties: {
      ...(isBuildAndPrice ? {
        event: {
          valueEventCode: getCustomValueEventCode()
        },
        vehicle: getCDLVehicleTransformer({
          ...vehicleData,
          ...selectedVrsData,
          trimSelected,
          fuelType: vehicleData.fuelType ?? selectedVrsData?.engineType
        }),
        vehicleSearch: getCDLVehicleSearch(dataLayer)
      } : {
        event: {
          valueEventCode: ddpValueEventCode || getCustomValueEventCode()
        },
        ...customProductConfiguration
      })
    }
  };
  if (par === callSellerPar) {
    label = labelCallSeller;
    cdl.eventProperties = {
      vehicle: PixallInventoryTransformer.getVehicleCommonDataLayer(inventoryWithOwner, includeWalletData),
      event: {
        valueEventCode: ddpValueEventCode || getCustomValueEventCode()
      }
    };
    googleProps = {
      eventName: 'phone',
      eventElement: 'dealer',
      eventAction: 'submit',
      eventResult: 'lead::phone::alpha',
      inheritPageData: false,
      eventData: {
        event_value_code: ddpValueEventCode || getCustomValueEventCode(),
        vehicle: {
          ...eventData.vehicle
        }
      }
    };
  }
  if (data.valueEventCode) {
    cdl.eventProperties.event.valueEventCode = data.valueEventCode;
  }
  if (isWallet) {
    googleProps = {
      eventElement: 'dealer',
      eventAction: 'click',
      eventResult: 'lead::phone::my-wallet-deals',
      eventData: {
        ...eventData,
        vehicle: {
          ...eventData.vehicle,
          productConfiguration: 'my-wallet'
        }
      }
    };
    label = labelCallSeller;
    cdl.eventProperties.vehicle = PixallInventoryTransformer.getVehicleCommonDataLayer(inventoryWithOwner, includeWalletData);
  }

  // premium spotlight alert or peekaboo
  if (pixallData?.pixallContext === 'top-spv' || pixallData?.pixallContext === 'peekaboo-spv') {
    label += ` - ${pixallData.pixallContext}`;
    delete pixallData.pixallContext;
  }
  if (isPremiumSpotlight(par)) {
    pixallData.listingType = 'highlight';
  }
  addListingTriggerData(par, cdl);
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    listingType,
    label,
    leadType,
    eventType: 'click',
    eventSource: 'lead',
    feature: 'Drive the 5',
    goal,
    inventory: inventoryWithOwner,
    listingCode: ownerId,
    par,
    ...(isBuildAndPrice && {
      pageType: 'buildprice',
      vehicleType: 'NEW'
    }),
    cdl
  }, dataLayer, option);
  const payload = {
    cmp,
    co_txt_url: 'Call Dealer',
    par,
    ...(isBuildAndPrice && {
      pageType: 'buildprice'
    })
  };
  Google.sendClickEvent({
    ...googleProps
  }, option);

  // return data for custom event
  return payload;
}
function getTextLink(labelKey) {
  switch (labelKey) {
    case 'virtualTour':
      return 'text-link::set-up-a-time';
    case 'testDrive':
      return 'text-link::request-a-test-drive';
    case 'vehicleDelivery':
      return 'text-link::contact-dealer-for-details';
    default:
      return '';
  }
}
export const emailCTAMap = {
  alpha: {
    cmp: 'eml_slr_a'
  },
  availableOffers: {
    // VDP available offers fair market range
    eventElement: 'dealer',
    eventResult: 'text-link::confirm-availability'
  },
  calculator: {
    // VDP NDS
    cmp: 'snd_dlr_ofr',
    eventResult: 'cta::send-offer',
    eventElement: 'nds',
    feature: 'NDS'
  },
  calculatorOfferSummary: {
    cmp: 'snd_dlr_ofr',
    eventResult: 'cta::send-offer',
    eventElement: 'nds',
    feature: 'NDS'
  },
  compare: {
    // SRP compare modal confirm availability
    cmp: 'eml_dlr_lnk',
    eventResult: 'text-link::confirm-availability',
    clickType: 'compare',
    isCompareModel: true
  },
  compareSendOffer: {
    // SRP compare modal send offer
    cmp: 'snd_offer',
    feature: 'Compare',
    coTxtUrl: 'send offer',
    eventElement: 'compare',
    eventResult: 'cta::send-offer',
    isCompareModel: true
  },
  confirmAvailability: {
    // default
    cmp: 'eml_dlr_lnk',
    eventResult: 'text-link::confirm-availability'
  },
  contactTheDealer: {
    cmp: 'eml_dlr_lnk'
  },
  creditStatus: {
    // VDP NDS You have already applied for credit. Contact Dealer
    cmp: 'nds_con_dlr_crd_app',
    coTxtUrl: 'Contact Dealer Credit Application Applied',
    eventResult: 'text-link::caca-saca::contact-dealer',
    eventElement: 'nds',
    pixallLabel: 'con_dlr_crd_appld'
  },
  inventory: {
    cmp: 'eml_slr'
  },
  longHaulDeliveryLongForm: {
    cmp: 'con_dlr_arv_shp',
    eventElement: 'dhs'
  },
  longHaulDeliveryShortForm: {
    cmp: 'ask_dlr',
    eventElement: 'dhs'
  },
  protection: {
    // VDP Optional Add-On Protections
    cmp: 'cnct_dlr',
    coTxtUrl: 'Contact Dealer',
    eventResult: 'cta::contact-dealer::additional-protections'
  },
  quote: {
    // SRP
    cmp: 'reqst_quote',
    coTxtUrl: 'Request a Quote',
    feature: 'New Car Order',
    eventResult: 'cta::request-a-quote::new-car-custom-order',
    clickType: 'newcarcustomorder',
    carType: 'n'
  },
  testDrive: {
    cmp: 'dlr_hm_sev_lnk',
    coTxtUrl: 'dhs:test drive',
    eventElement: 'dhs',
    eventResult: 'text-link::request-a-test-drive',
    isHomeService: true
  },
  vehicleDelivery: {
    cmp: 'dlr_hm_sev_lnk',
    coTxtUrl: 'dhs:deliver',
    eventElement: 'dhs',
    eventResult: 'text-link::contact-dealer-for-details',
    isHomeService: true
  },
  virtualTour: {
    cmp: 'dlr_hm_sev_lnk',
    coTxtUrl: 'dhs:video',
    eventElement: 'dhs',
    eventResult: 'text-link::set-up-a-time',
    isHomeService: true
  },
  NO_ACCIDENTS_NO_RECALL: {
    eventResult: 'cta::no-accidents-or-current-recalls::contact-dealer',
    pixallLabel: 'cnct_dlr - No Accidents or Current Recalls'
  },
  ACCIDENTS_REPORTED: {
    eventResult: 'cta::accident-detected::contact-dealer',
    pixallLabel: 'cnct_dlr - Accident Detected'
  },
  OPEN_RECALLS_REPORTED: {
    eventResult: 'cta::active-recall-present::contact-dealer',
    pixallLabel: 'cnct_dlr - Active Recall Present'
  },
  ACCIDENTS_RECALL_REPORTED: {
    eventResult: 'cta::active-recall-and-accident-detected::contact-dealer',
    pixallLabel: 'cnct_dlr - Active Recall and Accident Detected'
  },
  incentives: {
    feature: null
  },
  newlyListedPopover: {
    cmp: 'eml_dlr_lnk',
    feature: null
  },
  submitAnOffer: {
    cmp: 'eml_dlr_lnk',
    feature: null
  },
  reducedPriceNudge: {
    cmp: 'eml_dlr_lnk',
    feature: null
  }
};

/**
 * @summary Sends event when email link/button is clicked
 * @data emailOwnerData
 */
export function ownerEmailClick(_ref6, option) {
  let {
    event,
    data = {},
    state,
    dataLayer
  } = _ref6;
  const {
    clickType: defaultClickType,
    context = 'confirmAvailability',
    par = '',
    inventoryId,
    ownerId: defaultOwnerId
  } = data;
  let includePageId = data?.includePageId ?? true;
  const inventoryWithOwner = context === 'quote' ? _get(state, `preorder.${inventoryId}`, {}) : getInventoryOwnerById(state, inventoryId);
  const ownerId = _get(inventoryWithOwner, 'owner.id', '') || defaultOwnerId;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const pageName = _get(dataLayer, 'page.pageName', '');
  const inheritPageData = !['vdp'].includes(pageName);
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  const customerSource = _get(dataLayer, 'service.customerSource', '');
  const srpBoostActiveResults = _get(state, 'srp_boost.activeResults', []); // for new_car_boost in SRP page

  let defaultCoTxtUrl = (event?.target?.innerText || event?.target?.textContent || '').trim();
  let defaultEventElement = 'dealer';
  let placement = '';
  switch (context) {
    case 'inventory':
      includePageId = false;
      break;
    case 'calculator':
      defaultCoTxtUrl = `${state?.payments?.dealType} ${event?.target?.innerText ?? 'Send Offer'}`;
      break;
    case 'protection':
    case 'NO_ACCIDENTS_NO_RECALL':
    case 'ACCIDENTS_REPORTED':
    case 'OPEN_RECALLS_REPORTED':
    case 'ACCIDENTS_RECALL_REPORTED':
      defaultEventElement = `vdp_${inventoryWithOwner?.type.toLowerCase()}`;
      break;
    case 'ownerDetails':
      includePageId = false;
      placement = 'dealer-card';
      break;
    case 'stickyHeader':
      includePageId = false;
      placement = 'sticky-header';
      break;
    case 'mediaGallery':
      placement = 'media-gallery';
      break;
    case 'availableOffers':
      if (par === 'price-breakdown') {
        includePageId = false;
        placement = par;
        break;
      }
      placement = 'deal-dashboard';
      break;
    case 'compare':
      includePageId = false;
      placement = 'compare';
      break;
    case 'newlyListedPopover':
    case 'submitAnOffer':
      includePageId = false;
      placement = 'dealer-badge';
      break;
    case 'reducedPriceNudge':
      includePageId = false;
      placement = 'reduced-price-nudge';
      break;
    case 'incentives':
      if (par === 'pricing-breakdown') {
        placement = 'fair-market-range';
        break;
      } else {
        defaultCoTxtUrl = 'learn-more';
        placement = 'price-card';
        break;
      }
    case 'contactTheDealer':
      includePageId = false;
      placement = 'nds';
      break;
    case 'inline':
      placement = 'inline';
      break;
    case 'vehicleHistoryReport':
      placement = 'vhr';
      includePageId = false;
      break;
    default:
      if (pageId === 'fyc_srl' && srpBoostActiveResults.includes(inventoryId)) {
        // Condition for new_car_boost in SRP
        includePageId = false;
        placement = 'ncb';
      } else if (par === 'price-card') {
        includePageId = false;
        placement = par;
      }
  }
  const {
    clickType = defaultClickType,
    cmp = 'eml_dlr_lnk',
    eventElement = defaultEventElement,
    eventResult = `text-link::${defaultCoTxtUrl.replace(/\s+/g, '-').toLowerCase()}`,
    feature = 'Drive the 5',
    isHomeService = false,
    isCompareModel = false,
    pixallLabel
  } = emailCTAMap[context] || emailCTAMap.confirmAvailability; // if context doesn't exist in the map, use default value confirmAvailability

  let label;
  let eventSource;
  let labelSuffix;
  let searchFacetVehicleTypes;
  if (includePageId && isCompareModel) {
    labelSuffix = `::${pageId}::compare`;
  } else if (includePageId && placement) {
    labelSuffix = `-${pageId} - ${placement}`;
  } else if (includePageId && !isCompareModel) {
    labelSuffix = ` - ${pageId}`;
  } else if (isCompareModel && placement) {
    labelSuffix = `::${placement}`;
  } else if (placement) {
    labelSuffix = `-${placement}`;
  } else {
    labelSuffix = '';
  }
  if (['fyd_ddp', 'dlrinfoinvovrnew'].includes(pageId)) {
    label = 'text-link::confirm-availability';
    eventSource = 'dealership';
    searchFacetVehicleTypes = inventoryWithOwner?.listingType;
  } else if (['incentives'].includes(context)) {
    eventSource = par === 'pricing-breakdown' ? 'link' : 'dealership';
    label = `${eventResult}::${placement}`;
  } else if (['moreInfo'].includes(context)) {
    label = 'eml_dlr_lnk - media gallery';
    eventSource = 'vehicle';
  } else if (['morePhotos'].includes(context)) {
    label = 'cta::request-more-photos';
    eventSource = 'vehicle';
  } else {
    label = `${pixallLabel || cmp}${labelSuffix}`;
    eventSource = 'vehicle';
  }
  const pixallData = {
    clickTypeToListingType: isVDP(dataLayer)
  };

  // premium spotlight alert or peekaboo
  if (pixallData?.pixallContext === 'top-spv' || pixallData?.pixallContext === 'peekaboo-spv') {
    label += ` - ${pixallData.pixallContext}`;
    delete pixallData.pixallContext;
  }
  if (context !== 'compareSendOffer') {
    const cdl = {
      eventProperties: {
        service: {
          customerSource
        },
        ...(context === 'moreInfo' && {
          action: 'Click',
          element: 'Request Info Button',
          elementText: 'Confirm Availability',
          location: 'Gallery',
          tagIndex: 'vdp-018'
        }),
        ...(context === 'morePhotos' && {
          action: 'Click',
          element: 'Request More Photos Button',
          elementText: 'Request More Photos',
          tagIndex: 'vdp-021'
        })
      }
    };
    if (isPremiumSpotlight(par)) {
      pixallData.listingType = 'highlight';
    }
    addListingTriggerData(par, cdl);
    Pixall.sendClickEvent({
      label,
      eventSource,
      ...(feature && {
        feature
      }),
      inventory: inventoryWithOwner,
      listingCode: ownerId,
      par,
      searchFacetVehicleTypes,
      ...(context === 'quote' && {
        eventSource: 'dealership',
        inventory: {
          ...inventoryWithOwner,
          type: 'new'
        }
      }),
      vehicleType: inventoryWithOwner?.listingType?.toUpperCase(),
      pixallData,
      cdl
    }, dataLayer, option);
  }
  if (!['newlyListedPopover', 'submitAnOffer', 'reducedPriceNudge', 'contactTheDealer', 'inline', 'vehicleHistoryReport'].includes(context)) {
    Google.sendClickEvent({
      eventElement,
      eventResult: placement ? `${eventResult}::${placement}` : eventResult,
      eventData: {
        vehicle: {
          clickType,
          ...(['vdp'].includes(pageName) && vehicleData),
          ...(ownerId && {
            dealershipId: ownerId?.toString()
          }),
          ...(isHomeService && transformProductConfig({
            homeServices: _get(inventoryWithOwner, 'owner.homeServices', {}),
            nds: hasPrice(inventoryWithOwner)
          }))
        }
      }
    }, option);
  }

  // return data for custom event
  return {
    cmp
  };
}

/**
 * DEPRECATED - please use ownerEmailClick
 * @summary Sends event when call to action email element is clicked
 * @data emailOwnerData
 * @deprecated
 */
export function ownerEmailCTAClick(_ref7, option) {
  let {
    // !!! DEPRECATED - PLEASE USE OWNEREMAILCLICK !!!
    event,
    data = {},
    state,
    dataLayer
  } = _ref7;
  // !!! DEPRECATED - PLEASE USE OWNEREMAILCLICK !!!
  const {
    clickType,
    par = '',
    inventoryId,
    ownerId,
    labelKey,
    disableFeature,
    pixallData = {}
  } = data;
  const isQuote = labelKey === 'quote';
  const inventoryWithOwner = isQuote ? _get(state, `preorder.${inventoryId}`, {}) : getInventoryOwnerById(state, inventoryId);
  const id = _get(inventoryWithOwner, 'owner.id', '') || ownerId;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const pageName = _get(dataLayer, 'page.pageName', '');
  const isCompare = labelKey === 'compare' || par === 'comp_lstg' && labelKey === 'confirmAvailability';
  const isHomeServices = labelKey === 'virtualTour' || labelKey === 'testDrive' || labelKey === 'vehicleDelivery';
  const isNDSCalc = labelKey === 'calculator';
  const isConfirmAvailabilityCTA = labelKey === 'confirmAvailabilityCTA';
  const isAdditionalProtection = labelKey === 'additionalProtections';
  const isBuildAndPrice = labelKey === 'buildAndPrice';
  const isLongHaulDelivery = labelKey === 'longHaulDeliveryLongForm' || labelKey === 'longHaulDeliveryShortForm';
  const isCreditStatus = labelKey === 'creditStatus';
  const inheritPageData = !['vdp'].includes(pageName);
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  let cmp = 'eml_dlr_lnk';
  let eventResult = 'text-link::confirm-availability';
  let eventData = clickType ? {
    vehicle: {
      clickType
    }
  } : {};
  let eventElement = 'dealer';
  let feature = 'Drive the 5';
  if (par === 'alp') {
    cmp = 'eml_slr_a'; // alpha
  } else if (isQuote) {
    cmp = 'reqst_quote';
    feature = 'New Car Order';
    eventResult = 'cta::request-a-quote::new-car-custom-order';
    eventData = {
      vehicle: {
        ...(['vdp'].includes(pageName) && vehicleData),
        clickType: 'newcarcustomorder',
        dealershipId: ownerId?.toString()
      }
    };
  } else if (isConfirmAvailabilityCTA) {
    eventResult = 'cta::confirm-availability';
    eventData = {
      vehicle: {
        dealershipId: ownerId?.toString()
      }
    };
  } else if (isCompare) {
    eventElement = 'compare';
    if (labelKey === 'confirmAvailability') {
      eventResult = 'cta::confirm-availability';
      eventData = {
        vehicle: {
          clickType: 'compare'
        }
      };
    } else {
      cmp = 'snd_offer'; // compare
      feature = 'Compare';
      eventResult = 'cta::send-offer';
      eventData = {
        vehicle: googleInventoryTransformer(inventoryWithOwner, {
          target: 'vehicle',
          dataLayer
        })
      };
    }
  } else if (isNDSCalc) {
    cmp = 'snd_dlr_ofr'; // nds
    eventResult = 'cta::send-offer';
    eventElement = 'nds';
    feature = 'NDS';
  } else if (isHomeServices) {
    cmp = 'dlr_hm_sev_lnk'; // dhs
    eventResult = getTextLink(labelKey);
    eventData = {
      vehicle: {
        ...googleInventoryTransformer(inventoryWithOwner, {
          target: 'vehicle',
          dataLayer
        }),
        ...transformProductConfig({
          homeServices: _get(inventoryWithOwner, 'owner.homeServices', {}),
          nds: hasPrice(inventoryWithOwner)
        })
      }
    };
    eventElement = 'dhs';
  } else if (pageName === 'ddp') {
    eventResult = 'icon::email'; // DE190193 GA360 - DDP Email Icon Click (EVENT ID 14)  has incorrect eventResult
    eventData = {
      dealershipId: ownerId
    }; // DE191158 GA360 - DDP Email Icon Click (EVENT ID 14)  has incorrect eventData
  } else if (isAdditionalProtection) {
    cmp = 'cnct_dlr';
    eventElement = `vdp_${inventoryWithOwner?.type.toLowerCase()}`;
    eventResult = 'cta::contact-dealer::additional-protections';
    eventData = {
      vehicle: {
        ...vehicleData
      }
    };
  } else if (isBuildAndPrice) {
    cmp = 'bld_pr';
    feature = 'New Car Order';
    eventElement = 'build-price';
    eventResult = 'cta::open::build-price';
    eventData = {
      vehicle: {
        clickType: 'newcarcustomorder'
      }
    };
  } else if (isLongHaulDelivery) {
    const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
    cmp = labelKey === 'longHaulDeliveryLongForm' ? 'con_dlr_arv_shp' : 'ask_dlr';
    eventElement = 'dhs';
    eventResult = `text-link::${textContent.replace(/\s+/g, '-').toLowerCase()}`;
    eventData = {
      vehicle: {
        ...vehicleData
      }
    };
  } else if (isCreditStatus) {
    eventData = {
      vehicle: {
        ...vehicleData
      }
    };
    cmp = 'nds_con_dlr_crd_app';
    eventResult = 'text-link::caca-saca::contact-dealer';
    eventElement = 'nds';
  }
  if (disableFeature) {
    feature = undefined;
  }
  let pixallLabelCmp = cmp;
  if (isCreditStatus) {
    pixallLabelCmp = 'con_dlr_crd_appld';
  }
  if (!isCompare) {
    Pixall.sendClickEvent({
      label: `${pixallLabelCmp} - ${pageId}`,
      eventSource: 'vehicle',
      feature,
      inventory: inventoryWithOwner,
      listingCode: id,
      par,
      ...((isQuote || isBuildAndPrice) && {
        eventSource: 'dealership',
        inventory: {
          ...inventoryWithOwner,
          type: 'new'
        }
      }),
      pixallData: {
        ...pixallData,
        clickTypeToListingType: isVDP(dataLayer)
      }
    }, dataLayer, option);
  }
  Google.sendClickEvent({
    eventElement,
    eventResult,
    eventData
  }, option);

  // return data for custom event
  return {
    cmp
  };
}

/**
 * @summary Sends event after email form is submitted
 * @data emailOwnerData
 */
export function ownerEmailSent(_ref8, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref8;
  const {
    ownerId,
    par = '',
    cmp = 'snd_eml',
    consumerMetadata,
    includePageId = true,
    inventoryId,
    testDrive,
    vehicleDelivery,
    virtualTour,
    includeWalletData,
    vdpType,
    formEventId,
    preorderInfo,
    serviceInquiry,
    context,
    subject,
    inquiryLabel = '',
    isChipEnabled = false,
    pixallData = {},
    selectedVrsData = {},
    trimSelected,
    hasListingType,
    isOneClick,
    isSimilarListingsEnabled = false,
    hasSavedListings = false
  } = data;
  const isQuote = context === 'quote';
  const inventoryWithOwner = isQuote ? _get(state, `preorder.models.${inventoryId}`, {}) : getInventoryOwnerById(state, inventoryId);
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const pageName = _get(dataLayer, 'page.pageName', '');
  const partnerName = state?.brand;
  const customerSource = _get(dataLayer, 'service.customerSource', '');
  let goal;
  let inheritPageData;
  if (includeWalletData) {
    goal = 'Sent My Wallet Offer';
  }
  let placement;
  let eventResult;
  let listingCategory;
  let abTestId;
  let searchFacetVehicleTypes;
  let labelSuffix = includePageId ? ` - ${pageId}` : '';
  if (['alp', 'alp-dlr', 'alpm', 'alpm-dlr'].includes(par)) {
    labelSuffix = `${labelSuffix} - ${par}`;
  }

  // add cmp here
  let label = `Email Dealer - ${cmp}${labelSuffix}`;
  const eventAction = 'submit';
  const eventType = 'submit';
  const eventSource = 'lead';
  let feature = 'Drive the 5';
  const isCompare = vdpType === 'Compare';
  const isBuildAndPrice = context === 'buildAndPrice';
  const isLongHaulDelivery = context?.includes('longHaul');
  const isNDSCalc = ['calculator', 'calculatorOfferSummary', 'calculatorDealSubmitted'].includes(context);
  const isWallet = context === 'wallet';
  const isIncentives = context === 'incentives';
  switch (pageName) {
    case 'vdp':
      eventResult = 'lead::email::inline';
      placement = 'inline';
      if (testDrive || vehicleDelivery || virtualTour) {
        eventResult = 'lead::email::dhs';
        placement = 'dhs';
      }
      if (isNDSCalc) {
        eventResult = 'lead::email::nds';
        label = cmp;
        feature = vdpType;
        placement = 'nds';
      }
      if (preorderInfo) {
        eventResult = 'lead::email::new-car-custom-order';
        placement = 'new-car-custom-order';
        inheritPageData = false;
      }
      if (isCompare) {
        listingCategory = 'compare';
      }
      if (context === 'mediaGallery') {
        eventResult = 'lead::email::media-gallery';
        placement = 'media-gallery';
      }
      if (context === 'stickyHeader') {
        eventResult = 'lead::email::sticky-header';
        placement = 'sticky-header';
      }
      if (context === 'ownerDetails') {
        eventResult = 'lead::email::dealer-card';
        placement = 'dealer-card';
      }
      if (['compare', 'compareSendOffer'].includes(context)) {
        eventResult = 'lead::email::compare';
        placement = 'compare';
      }
      if (context === 'additionalProtections') {
        eventResult = 'lead::email::additional-protection-modal';
        placement = 'additional-protection-modal';
      }
      if (context === 'contactTheDealer') {
        eventResult = 'lead::email::nds';
        placement = 'nds';
      }
      if (context === 'emailConfirmation') {
        eventResult = 'lead::email::nba';
        placement = 'nba';
        if (hasSavedListings) {
          label = `Email Dealer - snd_eml-${placement}::saved-vehicle`;
        } else if (isSimilarListingsEnabled) {
          label = `Email Dealer - snd_eml-${placement}::similar`;
        } else {
          label = `Email Dealer - snd_eml-${placement}::recently-viewed`;
        }
      }
      if (context === 'vehicleHistoryReport') {
        eventResult = 'lead::email::vhr';
        placement = 'vhr';
      }
      if (isIncentives) {
        placement = 'offer-modal';
      }
      if (isWallet) {
        eventResult = 'lead::email::my-wallet-deals';
        goal = 'Sent My Wallet Offer';
      }
      if (isOneClick) {
        label = 'Email Dealer - snd_eml-one-click';
        eventResult = `lead::email::${placement}::one-click`;
      }
      break;
    case 'srp':
      if (preorderInfo) {
        listingCategory = 'new-car-custom-order';
        placement = 'new-car-custom-order';
      } else {
        listingCategory = getListingCategory(par);
      }
      if (context === 'emailConfirmation') {
        placement = 'nba';
      }
      if (isOneClick) {
        label = `Email Dealer - snd_eml-${pageId}-one-click`;
      }
      eventResult = `lead::email::${listingCategory}${isOneClick ? '::one-click' : ''}`;
      break;
    case 'ddp':
    case 'dlrinfoinvovrnew':
      eventResult = `lead::email::${sanitizeValue(subject)}`;
      break;
    case 'bap':
      eventResult = 'lead::email::build-price';
      delete inventoryWithOwner.id;
      break;
    case 'inv':
      label = `Email Dealer - ${cmp}`;
      eventResult = `lead::email::${pixallData.listingType || data.listingType || listingTypeUtils.getListingType(pageId, par)}`;
      break;
    default:
      eventResult = 'lead::email';
  }
  const inquirySelection = inquiryLabel.trim().replace(/\b's\b/g, '');
  const payload = {
    linkName: isCompare || isLongHaulDelivery ? 'Link Track' : 'Email DT5',
    leadType: 'email'
  };
  if (serviceInquiry === 'scheduleTestDrive' && !isChipEnabled) {
    if (partnerName === 'kbb') {
      payload.linkName = 'Request Appointment';
    } else {
      delete payload.linkName;
      payload.page = 'fyc_srl';
      payload.pageType = 'vlp';
    }
    label = `${label} - Request_appt`;
  }
  if (isChipEnabled && pageId !== 'fyc_srl' && pageId !== 'bld_and_pr' && pageName !== 'ddp' && pageName !== 'dlrinfoinvovrnew' && pageName !== 'inv' && !isIncentives) {
    payload.productType = inquirySelection;
    label = `${label} - ${inquirySelection}`;
  }
  const {
    productConfiguration = ''
  } = transformProductConfig({
    homeServices: {
      testDrive,
      vehicleDelivery,
      virtualTour,
      serviceInquiry
    },
    wallet: includeWalletData || context === 'wallet',
    nds: isNDSCalc
  });
  if (isCompare) {
    label = `Email Dealer - snd_eml - ${pageId} - compare`;
  }
  const productConfig = getProductConfiguration(inventoryWithOwner, includeWalletData);
  const hasMyWalletIncluded = productConfig?.includes('my-wallet');
  const getCustomValueEventCode = () => {
    let customValueEventCode = valueEventCode.EMAIL_LEAD;
    if (hasMyWalletIncluded) {
      customValueEventCode = isBuildAndPrice ? valueEventCode.BAP_EMAIL_LEAD_WITH_MY_WALLET : valueEventCode.EMAIL_LEAD_WITH_MY_WALLET;
    } else if (placement === 'inline' && serviceInquiry === 'scheduleTestDrive') {
      customValueEventCode = valueEventCode.LEAD_WITH_TEST_DRIVE;
    }
    return customValueEventCode;
  };
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  let cdl = {
    eventProperties: {
      event: {
        valueEventCode: getCustomValueEventCode()
      },
      ...(isBuildAndPrice && {
        vehicle: getCDLVehicleTransformer({
          ...vehicleData,
          ...selectedVrsData,
          trimSelected,
          fuelType: vehicleData?.fuelType ?? selectedVrsData?.engineType
        }),
        vehicleSearch: getCDLVehicleSearch(dataLayer)
      })
    }
  };
  if (pageName === 'dlrinfoinvovrnew') {
    cdl = {
      eventProperties: {
        event: {
          ...cdl.eventProperties.event
        },
        vehicle: {
          productConfiguration
        },
        service: {
          customerSource
        }
      }
    };
  }
  if (isWallet || isOneClick) {
    if (!isOneClick) {
      label = 'Email Dealer - snd_eml';
    }
    cdl.eventProperties.vehicle = PixallInventoryTransformer.getVehicleCommonDataLayer(inventoryWithOwner, includeWalletData);
    cdl.eventProperties.event.valueEventCode = hasMyWalletIncluded ? valueEventCode.EMAIL_LEAD_WITH_MY_WALLET : valueEventCode.EMAIL_LEAD;
  }
  if (context === 'mediaGallery') {
    cdl.eventProperties = {
      ...cdl.eventProperties,
      action: 'Click',
      element: 'Email Lead Form Button',
      conversionContext: 'Contact Dealer',
      conversionGoal: hasMyWalletIncluded ? 'Email with My Wallet Data' : '',
      conversionType: 'Email',
      elementText: 'Send Email',
      location: 'Gallery',
      tagIndex: hasMyWalletIncluded ? 'vdp-002' : 'vdp-001'
    };
  }
  if (isIncentives) {
    eventResult = 'lead::email::offer-modal';
    ({
      abTestId
    } = DataLayerTransformer.getPageAttributes(dataLayer));
  }
  if (isIncentives || isOneClick || pageName === 'ddp' || pageName === 'dlrinfoinvovrnew') {
    vehicleData.valueEventCode = getCustomValueEventCode();
  }
  if (placement && !isOneClick && placement !== 'nba') {
    label = `Email Dealer - snd_eml-${placement}`;
  }

  // premium spotlight alert or peekaboo
  if (pixallData?.pixallContext === 'top-spv' || pixallData?.pixallContext === 'peekaboo-spv') {
    label += ` - ${pixallData.pixallContext}`;
    delete pixallData.pixallContext;
  }
  if (isPremiumSpotlight(par)) {
    pixallData.listingType = 'highlight';
  }
  addListingTriggerData(par, cdl);
  const pixallPayload = {
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    consumerMetadata,
    label,
    leadType: 'form',
    leadContext: 'email',
    hasListingType,
    eventType,
    eventSource,
    feature,
    ...((pageName === 'ddp' || pageName === 'dlrinfoinvovrnew') && {
      odometer: undefined
    }),
    goal,
    inventory: inventoryWithOwner,
    listingCode: ownerId,
    par,
    searchFacetVehicleTypes,
    formEventId,
    abTestId,
    ...(isBuildAndPrice && {
      pageType: 'buildprice',
      year: vehicleData?.year ?? ''
    }),
    ...(isCompare && pageName === 'srp' && {
      listingType: 'compare'
    }),
    ...(pageName === 'inv' && partnerName === 'atc' && {
      listingType: 'hybrid listing'
    }),
    cdl
  };
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventAction,
    eventResult,
    eventData: {
      vehicle: pageName === 'ddp' ? {
        dealershipId: ownerId?.toString(),
        ...cleanValues(vehicleData),
        productConfiguration
      } : {
        ...vehicleData,
        productConfiguration,
        ...(listingCategory && {
          clickType: listingCategory
        })
      }
    }
  }, option);

  // return data for custom event
  return {
    cmp
  };
}
export function oneClickButtonClick(_ref9, option) {
  let {
    data = {},
    filterValues,
    state,
    dataLayer
  } = _ref9;
  const isVdp = isVDP(dataLayer);
  const {
    listingId,
    element = 'button',
    content = '',
    clickType = ''
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const par = _get(state, 'srp_activeInteraction.parentId', '');
  const label = `${element}::one-click::${content}`;
  const cdl = addListingTriggerData(par);
  const pixallPayload = {
    cdl,
    label,
    eventSource: 'button',
    filterValues,
    inventory,
    listingType: clickType,
    pixallData: {
      clickTypeToListingType: isVdp
    }
  };
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  if (!isVdp) {
    const googlePayload = {
      eventResult: label,
      eventElement: 'preference',
      eventData: {
        vehicle: {
          ...googleInventoryTransformer(inventory, {
            target: 'vehicle',
            dataLayer
          }),
          clickType
        }
      }
    };
    Google.sendClickEvent(googlePayload, option);
  }
  return {};
}

/**
 * @summary Sends event when visit website button is clicked
 * @data ownerId
 */
export function ownerWebsite(_ref0, option) {
  let {
    data = {},
    state,
    dataLayer,
    ...rest
  } = _ref0;
  const pageName = _get(dataLayer, 'page.pageName', '');
  const {
    ownerId,
    par,
    inventoryId,
    disablePixall,
    customerType,
    priorityType,
    isPageIdIncluded
  } = data;
  const {
    id
  } = _get(state, `owners.${ownerId}`, {});
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const detailPageName = dataLayer.page?.detailPageName; // GA detailPageName
  const inheritPageData = !['vdp'].includes(detailPageName);
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  const isEsntial = !!_get(inventoryWithOwner, 'esntial', false);
  const isAccelerate = _get(inventoryWithOwner, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const pageNameList = ['vdp', 'ddp', 'fyc', 'fyd'];
  let clickType = _get(state, 'query.clickType');
  if (par === 'alp') {
    clickType = 'alpha';
  } else if (par === 'alp-dlr') {
    clickType = 'alpha-dealer';
  } else if (par === 'alpm') {
    clickType = 'alpham';
  } else if (par === 'alpm-dlr') {
    clickType = 'alpham-dealer';
  }
  let eventResult = pageNameList.includes(pageName) ? 'sitelink' : `sitelink::${clickType}`;
  let eventElement = pageNameTransformer(pageName) || 'search-car';
  const pixallData = {
    listingCode: id
  };
  if (clickType === undefined) {
    clickType = data?.clickType;
  }
  if (clickType === 'recommendations') {
    pixallData.clickType = clickType;
  }
  if (pageName === 'fyd_srl') {
    pixallData.listingType = clickType;
    eventResult = `sitelink::${pixallData?.listingType}`;
    eventElement = 'dealer';
  }

  // return data for custom event
  return ownerWebsiteClickHelper({
    data: {
      ...data,
      cmp: 'vw_wb_btn'
    },
    isPageIdIncluded,
    pixallData,
    googleData: {
      eventElement,
      eventResult,
      eventData: {
        vehicle: pageName === 'ddp' ? {
          dealershipId: ownerId?.toString(),
          customerType,
          priorityType
        } : {
          ...vehicleData,
          clickType,
          dealershipId: ownerId?.toString()
        }
      }
    },
    state,
    isEsntial,
    isAccelerate,
    disablePixall,
    dataLayer,
    ...rest
  }, option);
}

/**
 * @summary Sends event when Get a Price Quote button is clicked
 * @data ownerId
 */
export function ownerPriceQuoteClick(_ref1, option) {
  let {
    data = {},
    state,
    dataLayer,
    ...rest
  } = _ref1;
  const {
    ownerId,
    par,
    pixallData,
    hasListingType = true,
    searchFacetDistance
  } = data;
  const clickType = 'click';
  const eventResult = 'cta:get-a-price-quote';
  const eventElement = 'search-dealer';
  Google.sendClickEvent({
    event: 'analytics_event',
    eventElement,
    eventAction: clickType,
    eventResult,
    eventNonInteractive: false,
    eventData: {
      vehicle: {
        dealershipId: ownerId?.toString()
      }
    }
  }, option);
  if (pixallData) {
    Pixall.sendClickEvent({
      pixallData,
      label: eventResult,
      eventSource: 'dealership',
      listingCode: ownerId,
      searchFacetDistance,
      hasListingType,
      par
    }, dataLayer, option);
  }
  return {
    ownerId: ownerId?.toString()
  };
}

/**
 * @summary Sends event on phone number reveal
 * @data ownerId, inventoryId
 */
export function ownerPhoneNumber(_ref10, option) {
  let {
    event,
    data,
    state,
    dataLayer
  } = _ref10;
  const {
    ownerId,
    inventoryId,
    par = '',
    cmp: customCmp,
    clickType = '',
    paginatedListingIndex
  } = data;
  const cmp = customCmp || 'gp';
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const pageName = _get(state, 'currentPageName', '');
  const eventText = _get(event, 'target.innerText', '');
  const linkCopy = _kebabCase(eventText);
  const listingType = isPremiumSpotlight(par) ? 'highlight' : undefined;
  const cdl = addListingTriggerData(par);
  let eventData;
  switch (pageName) {
    case 'srp':
      eventData = {
        vehicle: {
          ...googleInventoryTransformer(inventoryWithOwner, {
            target: 'vehicle',
            dataLayer
          }),
          clickType,
          listingPosition: paginatedListingIndex
        }
      };
      break;
    case 'dealerdetails':
      eventData = {
        vehicle: {
          dealershipId: ownerId,
          listingPosition: paginatedListingIndex
        }
      };
      break;
    case 'vdp':
      eventData = {
        vehicle: {
          ...googleInventoryTransformer(inventoryWithOwner, {
            target: 'vehicle',
            dataLayer
          })
        }
      };
      break;
    default:
      eventData = {};
      break;
  }
  Pixall.sendClickEvent({
    cdl,
    label: `Click to call - ${cmp} - ${par}`,
    eventSource: 'dealership',
    feature: 'Drive the 5',
    inventory: inventoryWithOwner,
    listingCode: ownerId,
    par,
    listingType,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventResult: `text-link::${linkCopy}`,
    eventData
  }, option);
  // return data for custom event
  return {
    cmp,
    par
  };
}

/**
 * @summary Sends event on get direction link
 * @data ownerId
 */
export async function ownerLocation(_ref11, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref11;
  const {
    cmp: customCmp,
    ownerId,
    par = '',
    inventoryId,
    placement,
    pixallData = {},
    selectedVrsData = {},
    trimSelected,
    hasListingType
  } = data;
  const owner = _get(state, `owners.${ownerId}`, {});
  const {
    id
  } = owner;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  });
  const cmp = customCmp || 'cs_view_map';
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const clickType = _get(state, 'query.clickType', getListingCategory(par));
  const pageName = _get(dataLayer, 'page.pageName', '');
  const isBuildAndPrice = pageName === 'bap';
  let label = `Maps - ${cmp} - ${pageId}`;
  if (pageName === 'vdp' && placement === 'snapshot') {
    label = `${cmp}-${pageId}`;
  }
  if (pageId === 'bld_and_pr') {
    label = 'Maps - get_dir_-bld_pr';
  }
  const cdl = {
    eventProperties: {
      vehicle: getCDLVehicleTransformer({
        ...vehicleData,
        ...selectedVrsData,
        trimId: trimSelected,
        fuelType: vehicleData.fuelType ?? selectedVrsData?.engineType
      }),
      vehicleSearch: getCDLVehicleSearch(dataLayer)
    }
  };
  addListingTriggerData(par, cdl);
  const listingType = isPremiumSpotlight(par) ? 'highlight' : undefined;
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: pageName === 'vdp'
    },
    label,
    eventSource: 'directions',
    feature: 'Drive the 9',
    listingCode: id,
    inventory: inventoryWithOwner,
    par,
    hasListingType,
    listingType,
    ...(['bap', 'srp'].includes(pageName) && {
      pageType: isBuildAndPrice ? 'buildprice' : 'vlp',
      cdl
    })
  }, dataLayer, option);
  let eventResult;
  switch (pageName) {
    case 'srp':
      eventResult = `directions::${clickType}`;
      break;
    case 'bap':
      eventResult = 'directions::build-price';
      break;
    default:
      eventResult = 'directions';
  }
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventAction: 'click',
    eventResult,
    eventData: {
      vehicle: pageName === 'ddp' ? {
        dealershipId: ownerId?.toString()
      } : {
        ...vehicleData,
        clickType
      }
    }
  }, option);

  // return data for custom event
  return {
    cmp
  };
}

/**
 * @summary Sends event on Text Owner
 * @data ownerId
 */
export function ownerText(_ref12, option) {
  let {
    data,
    state,
    dataLayer
  } = _ref12;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const {
    ownerId,
    par = '',
    inventoryId,
    placement,
    pixallData = {}
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const {
    id
  } = _get(state, `owners.${ownerId}`, {});
  const {
    payments
  } = state;
  const {
    interactions
  } = payments;
  const pageName = _get(dataLayer, 'page.pageName', '');
  const {
    hasUpdatedNDSCalc,
    hasOpenedWallet,
    hasOpenedTradeIn
  } = interactions;
  const includeWalletData = hasUpdatedNDSCalc || hasOpenedWallet || hasOpenedTradeIn;
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, true, includeWalletData);
  let goal;
  if (includeWalletData) {
    goal = 'Text with My Wallet Data';
  }
  let cmp = 'msg_slr';
  if (par?.includes('alp')) {
    cmp = 'msg_slr_a';
  }
  let label = `Chat Dealer - ${cmp} - ${pageId}`;
  if (pageName === 'vdp' && placement === 'snapshot') {
    label = cmp;
  }
  let googleProps = {
    eventElement: 'dealer',
    eventAction: 'submit',
    eventResult: 'lead::text',
    eventData: {
      vehicle: {
        ...vehicleData
      }
    }
  };
  if (placement === 'myWallet') {
    googleProps = {
      eventElement: 'dealer',
      eventAction: 'click',
      eventResult: 'lead::text::my-wallet-deals',
      eventData: {
        vehicle: {
          ...vehicleData,
          productConfiguration: 'my-wallet'
        }
      }
    };
  }
  if (placement === 'sticky-header' || placement === 'media-gallery') {
    label = `${cmp}::${placement}`;
    const {
      owner,
      accelerate,
      creditApp,
      isWestlakePrequal
    } = inventoryWithOwner;
    const {
      productConfiguration
    } = transformProductConfig({
      homeServices: {
        ...owner?.homeServices,
        accelerate
      },
      shippingLabel: owner?.shippingLabel,
      nds: hasPrice(inventoryWithOwner),
      westlake: isWestlakePrequal,
      creditApp: creditApp || accelerate
    }, includeWalletData);
    googleProps = {
      eventElement: 'dealer',
      eventAction: 'submit',
      eventResult: `lead::text::${placement}`,
      eventData: {
        vehicle: {
          productConfiguration,
          valueEventCode: productConfiguration.includes('my-wallet') ? valueEventCode.TEXT_LEAD_WITH_MY_WALLET : valueEventCode.TEXT_LEAD
        }
      }
    };
  }
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    label,
    eventSource: 'lead',
    feature: 'Drive the 5',
    goal,
    inventory: inventoryWithOwner,
    leadType: 'text',
    listingCode: id,
    par,
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: getProductConfiguration(inventoryWithOwner, includeWalletData)?.includes('my-wallet') ? valueEventCode.TEXT_LEAD_WITH_MY_WALLET : valueEventCode.TEXT_LEAD
        }
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    ...googleProps
  }, option);

  // return data for custom event
  return {
    cmp
  };
}

/**
 * @summary Sends event on Chat Dealer button is clicked and Chat Box (Third Party)
 * @data ownerId
 */
export function ownerChat(_ref13, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref13;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const pageName = _get(dataLayer, 'page.pageName', '');
  const pageType = _get(dataLayer, 'page.pageType', '');
  const {
    inventoryId,
    ownerId,
    par: customPar = '',
    cmp: customCmp,
    placement,
    pixallData = {},
    assistChannel,
    assistProduct,
    assistSubchannel
  } = data;
  const {
    id = ''
  } = _get(state, `owners.${ownerId}`, {});
  const {
    payments
  } = state;
  const {
    interactions
  } = payments;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const {
    hasUpdatedNDSCalc,
    hasOpenedWallet,
    hasOpenedTradeIn
  } = interactions;
  const includeWalletData = hasUpdatedNDSCalc || hasOpenedWallet || hasOpenedTradeIn;
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'vehicle',
    dataLayer
  }, true, includeWalletData);
  const {
    productConfiguration,
    dealershipId
  } = vehicleData;
  const ddpValueEventCode = valueEventCode.CHAT_LEAD;
  let goal;
  if (includeWalletData) {
    goal = 'Chat with My Wallet Data';
  }
  const cmp = customCmp || 'cs_chat';
  const par = customPar || 'v_lstg';
  const isPrivateSeller = inventoryWithOwner?.owner?.privateSeller;
  let label = `Chat Dealer - ${cmp} - ${pageId}`;
  if (pageName === 'vdp') {
    if (placement === 'snapshot') label = cmp;
    if (placement === 'shopasst') label = `${cmp}-shopasst`;
  }
  const includeMyWallet = getProductConfiguration(inventoryWithOwner, includeWalletData)?.includes('my-wallet');
  const customValueEventCode = includeMyWallet ? valueEventCode.CHAT_LEAD_WITH_MY_WALLET : valueEventCode.CHAT_LEAD;
  const psxPixallData = isPrivateSeller ? {
    label: 'cs_chat',
    feature: 'private-seller',
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: 'GS6F'
        }
      }
    }
  } : {};
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    inventory: inventoryWithOwner,
    label,
    eventSource: 'lead',
    feature: 'Drive the 5',
    goal,
    leadType: 'chat',
    listingCode: id,
    par,
    ...(pageName === 'ddp' && {
      vehicleId: undefined
    }),
    hasListingType: !(pageName === 'ddp' || pageName === 'dlrinfoinvovrnew'),
    pageType,
    assistChannel,
    assistProduct,
    assistSubchannel,
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: pageName === 'ddp' ? ddpValueEventCode : customValueEventCode
        }
      }
    },
    ...psxPixallData
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: isPrivateSeller ? 'private-seller' : 'dealer',
    eventAction: 'submit',
    eventResult: 'lead::chat',
    eventData: {
      vehicle: pageName === 'ddp' ? {
        productConfiguration,
        dealershipId
      } : {
        ...vehicleData,
        cj_ga_event_value_code: isPrivateSeller && 'GS6F'
      }
    }
  }, option);

  // return data for custom event
  return {
    cmp
  };
}

/**
 * @deprecated
 */
export function ownerViewOfferInventory() {
  return {};
}
export function ownerServiceInquiry(_ref14, option) {
  let {
    data = {},
    event,
    state,
    dataLayer
  } = _ref14;
  const {
    brand = ''
  } = state;
  const {
    inventoryId,
    inquiryLabel = ''
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const listingType = inventoryWithOwner?.listingType ? inventoryWithOwner?.listingType.toLowerCase() : (data?.listingType || '').toLowerCase();
  const inquirySelection = inquiryLabel.trim().replace(/\b's\b/g, '');
  const cmp = 'intrst_selection';
  const linkCopy = _kebabCase(inquirySelection);
  const payload = {};
  payload.productType = inquirySelection;
  if (brand === 'kbb') {
    payload.co_txt_url = inquirySelection;
    payload.linkName = 'Interest Selection';
  }
  payload.cmp = cmp;
  const label = `cta::${linkCopy}`;
  Google.sendClickEvent({
    eventElement: `vdp_${listingType}`,
    eventResult: label,
    eventNonInteractive: false,
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventoryWithOwner, {
          target: 'vehicle',
          dataLayer
        })
      }
    }
  }, option);
  Pixall.sendClickEvent({
    label,
    eventSource: 'link',
    inventory: inventoryWithOwner,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends ecom event for Service Appointment CTA ecom impression
 * @data listingPosition, dealershipId, dealerDistance
 * Dimension 25, 33, 34
 */
export function serviceAppointmentCTAEcomImpression(metadata, option) {
  const {
    data,
    state,
    dataLayer
  } = metadata;
  const {
    owner: ownerData,
    inventoryId,
    listingPosition
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const {
    owner
  } = inventoryWithOwner;
  if (!owner?.id) {
    inventoryWithOwner.owner = ownerData;
  }
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'ecommerce',
    vehicleListingPosition: listingPosition,
    dataLayer
  });
  Google.sendImpressions(metadata, {
    event: 'ecom_event',
    eventElement: 'dealer',
    eventAction: 'ecom-impression',
    eventResult: 'service-appointment',
    eventNonInteractive: true,
    ecommerce: {
      impressions: {
        ...vehicleData,
        id: 'dealer_srp',
        name: 'dealer_srp',
        category: 'ecom_dealer',
        list: 'ecom_dealer'
      }
    }
  }, option);
  return {
    ownerId: ownerData?.id,
    inventoryId
  };
}

/**
 * @summary Sends ecom event for Service Appointment CTA ecom click
 * @data listingPosition, dealershipId, dealerDistance
 * Dimension 25, 33, 34
 */
export function serviceAppointmentCTAEcomClick(metadata, option) {
  const {
    data,
    state,
    dataLayer
  } = metadata;
  const {
    owner: ownerData,
    inventoryId,
    listingPosition
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const {
    owner
  } = inventoryWithOwner;
  if (!owner.id) {
    inventoryWithOwner.owner = ownerData;
  }
  const vehicleData = googleInventoryTransformer(inventoryWithOwner, {
    target: 'ecommerce',
    vehicleListingPosition: listingPosition,
    dataLayer
  });
  Google.sendClickEvent({
    event: 'ecom_event',
    eventElement: 'dealer',
    eventAction: 'ecom-click',
    eventResult: 'cta::service-appointment',
    eventNonInteractive: true,
    ecommerce: {
      impressions: {
        ...vehicleData,
        id: 'dealer_srp',
        name: 'dealer_srp',
        category: 'ecom_dealer',
        list: 'ecom_dealer'
      }
    }
  }, option);
  return {
    ownerId: ownerData?.id,
    inventoryId
  };
}

/**
 * @summary Sends event on service appointment cta submit analytic event
 * @data productConfiguration, dealershipId
 * Dimension 134, 193
 */
export function serviceAppointmentSubmit(_ref15, option) {
  let {
    data,
    state
  } = _ref15;
  const {
    owner,
    inventoryId
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const onlinePaperwork = inventoryWithOwner?.accelerate && hasPrice(inventoryWithOwner);
  const {
    productConfiguration
  } = transformProductConfig({
    homeServices: {
      ..._get(owner, 'homeServices', {}),
      accelerate: onlinePaperwork
    },
    shippingLabel: owner?.shippingLabel,
    nds: hasPrice(inventoryWithOwner)
  });
  Google.sendClickEvent({
    event: 'analytics_event',
    eventElement: 'dealer',
    eventAction: 'submit',
    eventResult: 'cta::service-appointment',
    eventNonInteractive: false,
    eventData: {
      vehicle: {
        dealershipId: owner?.id?.toString(),
        productConfiguration
      }
    }
  }, option);
  return {
    inventoryId
  };
}

/**
 * @summary Sends event on contact us button is clicked
 * @data owner, inventoryId
 * Dimension 134, 193
 */

export function ownerContactClick(_ref16, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref16;
  const {
    owner,
    inventoryId,
    includeWalletData,
    pixallData = {},
    hasListingType
  } = data;
  const inventoryWithOwner = getInventoryOwnerById(state, inventoryId);
  const eventResult = 'cta::email::contact-us';
  const {
    productConfiguration
  } = transformProductConfig({
    homeServices: _get(owner, 'homeServices', {}),
    wallet: includeWalletData,
    nds: hasPrice(inventoryWithOwner)
  });
  Pixall.sendClickEvent({
    eventSource: 'dealership',
    listingCode: owner?.id?.toString(),
    hasListingType,
    ...pixallData
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventAction: 'submit',
    eventResult,
    eventData: {
      vehicle: {
        dealershipId: owner?.id?.toString(),
        productConfiguration
      }
    }
  }, option);
  // return data for custom event
  return {
    cmp: eventResult
  };
}
export function smsOptClick(_ref17, option) {
  let {
    data,
    dataLayer,
    state
  } = _ref17;
  const {
    checked
  } = data;
  const inventoryId = dataLayer.vehicle?.id;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const pixallData = {
    eventSource: 'userAccount',
    label: `sms::${checked ? 'opt-in' : 'opt-out'}`,
    inventory
  };
  Pixall.sendClickEvent(pixallData, dataLayer, option);
  return pixallData;
}
export function valueYourVehicleClick(_ref18, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref18;
  const {
    placement = '',
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const eventResult = `text-link::value-your-vehicle::${placement}`;
  Pixall.sendClickEvent({
    label: `text-link_value-your-vehicle_${placement}`,
    eventSource: 'userAccount',
    listingCode: ownerId,
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult,
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        })
      }
    }
  }, option);
  return {
    cmp: eventResult
  };
}
export default {
  oneClickButtonClick,
  ownerAccelerateClick,
  ownerImpression,
  ownerImpressions,
  ownerClick,
  ownerLogoClick,
  ownerCall,
  ownerEmailClick,
  ownerEmailCTAClick,
  ownerEmailSent,
  ownerWebsite,
  ownerPhoneNumber,
  ownerLocation,
  ownerText,
  ownerChat,
  ownerViewOfferInventory,
  ownerServiceInquiry,
  serviceAppointmentSubmit,
  serviceAppointmentCTAEcomImpression,
  serviceAppointmentCTAEcomClick,
  ownerPriceQuoteClick,
  valueYourVehicleClick
};